import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Flex, 
    Text,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Button,
} from '@chakra-ui/react';
import CustomPageFilters from '../../components/ui/filter/PageFilters';
import CustomModal from '../../components/ui/modals';
import FileUploadWrapper from '../../components/ui/file/UploadWrapper';
import { documentTypes } from '../../utilities/dummy';
import { FileOptionType } from '../../types/ui';
import SvgIcon from '../../components/ui/icons/SvgIcon';
import CustomButton from '../../components/ui/buttons/Button';
import { BsChevronDown, BsTrash } from 'react-icons/bs';
import DocumentWrap from '../../components/documents/DocumentWrap';

export default function Documents() {
    const [sortValue, setSortValue] = useState('Newest');
    const [showDocUploadModal, setShowDocUploadModal] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [documentType, setDocumentType] = React.useState<FileOptionType>({
        key: 'pdf',
        label: 'PDF', 
        accept: 'application/pdf'
    });
    const [files, setFiles] = useState<File[] | []>([]);

    function handleSort(value: string) {
        setSortValue(value)
    }

    function handleFileChange(files: any) {        
        if (files){
            setFiles(files)
        }
    }

    function handleTypeChange(type: FileOptionType) {
        setDocumentType(type)
    }
    
    function handleFilesUpload() {
        if (!files) return;
        setShowDocUploadModal(false)
    }

    function computeFileCTAText() {
        switch(documentType.key) {
            case 'file':
            case 'folder':
                return `Choose a ${documentType.key}`
            case 'multiple_files':
                return 'Choose mutiple files'
            case 'pdf':
                return 'Choose PDF'
            default:
                return 'Choose a file'
        }
    }

    function computeReplaceFileText() {
        switch(documentType.key) {
            case 'file':
            case 'folder':
                return `Replace ${documentType.key}`
            case 'multiple_files':
                return 'Replace mutiple files'
            case 'pdf':
                return 'Replace PDF'
            default:
                return 'Replace file'
        }
    }

    function computeFileUploads({ isDragActive }: { isDragActive: boolean }): React.ReactNode {
        return (
            <>
                {!files.length ? (
                    <Flex direction={'column'} justifyContent='center' alignItems='center'>
                        <SvgIcon 
                            type='Upload'
                            viewBox="0 0 36 36" 
                            height='36px'
                            width='36px'
                            color='indigo'
                            styles={{ marginBottom: '1rem' }}
                        />
                        <Text size='xs' fontSize={'xs'} color='gray.500' marginBottom='2'>No files chosen, yet!</Text>
                        <CustomButton 
                            type='button'
                            cta='outline'
                            variant='outline'
                            color='blue.400'
                            colorScheme='blue'
                            size='sm'
                            label={computeFileCTAText()}
                        />
                    </Flex>
                ) : (
                    <Flex direction={'column'} justifyContent='center' alignItems='center'>
                        <Box marginBottom='4'>
                            {files.map(file => (
                                <Text size='sm' fontSize={'sm'} color='gray.500' marginBottom='2'>{file?.name}</Text>
                            ))}
                        </Box>
                        <Flex alignItems='center' marginTop='8' justifyContent='center' width={'100%'}>
                            <CustomButton 
                                type='button'
                                cta='outline'
                                variant='outline'
                                color='blue.400'
                                colorScheme='blue'
                                size='sm'
                                label={computeReplaceFileText()}
                            />
                            <IconButton
                                variant='ghost'
                                colorScheme='red'
                                aria-label='Delete file'
                                fontSize='20px'
                                icon={<BsTrash />}
                            />
                        </Flex>
                    </Flex>  
                )}
            </>
        )
    }

    useEffect(() => {
        const key = documentType.key;
        if (key) {
            setFiles([])
        }
    }, [documentType.key])
    
    return (
        <div>
            <Box>
                <CustomPageFilters 
                    title='users list'
                    isSearchDisabled={false}
                    fields={null}
                    isSearch
                    searchPlaceholder='Search documents'
                    isNewButton
                    isOpen={isFilterOpen}
                    onOpen={() => setIsFilterOpen(true)}
                    onClose={() => setIsFilterOpen(false)}
                    addNewObj={{
                        pageType: 'document',
                        onClick: () => setShowDocUploadModal(true)
                    }}
                    isSort
                    sortObj={{
                        sortFn: handleSort,
                        sortValue
                    }}
                />
            </Box>

            {/* content */}
            <DocumentWrap />
            
            {/* Add new document modal */}
            <CustomModal
                isOpen={showDocUploadModal}
                onCloseFn={() => setShowDocUploadModal(false)}
                isFooter={true}
                size={'xl'}
                headerTitle='Select a document to upload'
                footerPrimaryBtnLabel='Upload'
                footerPrimaryBtnFn={handleFilesUpload}
                footerPrimaryBtnDisabled={!files.length}
            >
                <Flex alignItems='center'>
                    <Text fontSize={'md'} marginRight='2' size='sm'>Document Type:</Text>
                    <Menu>
                        <MenuButton height='2.1rem' as={Button} fontSize='xs' rightIcon={<BsChevronDown/>}>
                            {documentType.label}
                        </MenuButton>
                        <MenuList>
                            {documentTypes.map((type) => (
                                <MenuItem key={type.key} onClick={() => handleTypeChange(type)}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Menu>
                </Flex>
                <Box>
                    <FileUploadWrapper 
                        onUpload={(file) => { handleFileChange!(file)}} 
                        isMultiple={documentType.key === 'multiple_files' ? true : false}
                        accept={documentType.accept}
                    >
                        {computeFileUploads({ isDragActive: true })}
                    </FileUploadWrapper>
                </Box>
            </CustomModal>
        </div>
    )
}